import { ComponentType, memo, Ref } from 'react';
import { tw } from 'twind';

import {
  Slot,
  ComponentWithFilters as GqlComponentWithFilters,
  Maybe,
  FeaturesGrid as FeaturesGridType,
} from '__generated__/graphql';
import { useInView } from 'hooks/useInView';
import { BodyContent } from 'ui/content/BodyContent';
import { BodyHeader } from 'ui/content/BodyHeader';
import { SplitHero } from 'ui/content/SplitHero';
import { FullBleedHero } from 'ui/content/FullBleedHero';
import { FeatureHero } from 'ui/content/FeatureHero';
import { VideoHero } from 'ui/content/VideoHero';
import { ProductCarousel } from 'ui/content/ProductCarousel';
import { ProductCarouselSkeleton } from 'ui/content/ProductCarouselSkeleton';
import { ProductComparison } from 'ui/content/ProductComparison';
import { FeaturesGrid } from 'ui/content/FeaturesGrid';
import { ProductGroupFeatures } from 'ui/content/ProductGroupFeatures';
import { QuoteCallout } from 'ui/content/QuoteCallout';
import { SideBySide } from 'ui/content/SideBySide';
import { TrendingInline } from 'ui/content/TrendingInline';
import { YouTubeVideoCarousel } from 'ui/content/YouTubeVideoCarousel';
import { StyliticsCarousel } from 'ui/content/StyliticsCarousel';
import { ProductRecommender } from 'ui/content/ProductRecommender';
import { LaunchCalendar } from 'ui/content/LaunchCalendar';
import { SignUpHeroBanner } from 'ui/content/SignUpFormHero';
import { InfoBanner } from 'ui/content/InfoBanner';
import { PixleeSimpleWidget } from 'ui/content/PixleeSimpleWidget';
import { Iframe } from 'ui/content/Iframe';
import { AdditionalContentSection } from 'ui/content/AdditionalContentSection';
import { AutomatedProductComparison } from 'ui/content/AutomatedProductComparison';
import { Quilt } from 'ui/content/Quilt';
import { HowToWearIt } from 'ui/content/HowToWearIt';
import { ComponentWithFilters } from '__generated__/graphql-sanity';
import { ManageHeadingTag } from 'ui/content/ContentHeading';
import { FaqModule } from 'ui/content/FaqModule';
import { WebCarousel } from 'ui/content/WebCarousel';
import { Missing } from 'ui/content/Missing';

const FallbackPlaceholder = () => <div className="min-h-[50vh]" />;

type ComponentAndClsPlaceholder = {
  name: string;
  component: ComponentType<any>;
  clsPlaceholder?: ComponentType<any>;
};

const components: ComponentAndClsPlaceholder[] = [
  {
    name: 'AdditionalContentSection',
    component: AdditionalContentSection,
    clsPlaceholder: FallbackPlaceholder,
  },
  {
    name: 'AutomatedProductComparison',
    component: AutomatedProductComparison,
    clsPlaceholder: FallbackPlaceholder,
  },
  {
    name: 'BodyContent',
    component: BodyContent,
    clsPlaceholder: FallbackPlaceholder,
  },
  {
    name: 'BodyHeader',
    component: BodyHeader,
    clsPlaceholder: FallbackPlaceholder,
  },
  {
    name: 'FeatureHero',
    component: FeatureHero,
    clsPlaceholder: FallbackPlaceholder,
  },
  {
    name: 'FeaturesGrid',
    component: FeaturesGrid,
    clsPlaceholder: FallbackPlaceholder,
  },
  {
    name: 'FullBleedHero',
    component: FullBleedHero,
    clsPlaceholder: FallbackPlaceholder,
  },
  {
    name: 'Iframe',
    component: Iframe,
    clsPlaceholder: FallbackPlaceholder,
  },
  {
    name: 'InfoBanner',
    component: InfoBanner,
  },
  {
    name: 'PixleeSimpleWidget',
    component: PixleeSimpleWidget,
    clsPlaceholder: FallbackPlaceholder,
  },
  {
    name: 'ProductCarousel',
    component: ProductCarousel,
    clsPlaceholder: ProductCarouselSkeleton,
  },
  {
    name: 'ProductComparison',
    component: ProductComparison,
    clsPlaceholder: FallbackPlaceholder,
  },
  {
    name: 'ProductGroupFeatures',
    component: ProductGroupFeatures,
    clsPlaceholder: FallbackPlaceholder,
  },
  {
    name: 'ProductRecommender',
    component: ProductRecommender,
    // load component as fallback to ensure this conditionally displayed and very
    // slow loading component achieves desired state before user scrolls. CLS
    // from this component after scrolling is high and a terrible UX. We need to
    // get it rendered asap.
    clsPlaceholder: ProductRecommender,
  },
  {
    name: 'LaunchCalendar',
    component: LaunchCalendar,
    clsPlaceholder: FallbackPlaceholder,
  },
  {
    name: 'QuoteCallout',
    component: QuoteCallout,
    clsPlaceholder: FallbackPlaceholder,
  },
  {
    name: 'Quilt',
    component: Quilt,
    clsPlaceholder: FallbackPlaceholder,
  },
  {
    name: 'SideBySide',
    component: SideBySide,
    clsPlaceholder: FallbackPlaceholder,
  },
  {
    name: 'SignUpForm',
    component: SignUpHeroBanner,
    clsPlaceholder: FallbackPlaceholder,
  },
  {
    name: 'SplitHero',
    component: SplitHero,
    clsPlaceholder: FallbackPlaceholder,
  },
  {
    name: 'StyliticsCarousel',
    component: StyliticsCarousel,
    clsPlaceholder: FallbackPlaceholder,
  },
  {
    name: 'HowToWearIt',
    component: HowToWearIt,
    clsPlaceholder: FallbackPlaceholder,
  },
  {
    name: 'TrendingInline',
    component: TrendingInline,
  },
  {
    name: 'VideoHero',
    component: VideoHero,
    clsPlaceholder: FallbackPlaceholder,
  },
  {
    name: 'YouTubeVideoCarousel',
    component: YouTubeVideoCarousel,
    clsPlaceholder: FallbackPlaceholder,
  },
  {
    name: 'FaqModule',
    component: FaqModule,
    clsPlaceholder: FallbackPlaceholder,
  },
  {
    name: 'WebCarousel',
    component: WebCarousel,
    clsPlaceholder: FallbackPlaceholder,
  },
];

export const getComponent = (
  typename: string | undefined
): ComponentAndClsPlaceholder =>
  components.find(({ name }) => name === typename) ?? {
    name: 'Missing',
    component: Missing,
    clsPlaceholder: FallbackPlaceholder,
  };

export const InView = ({ i, data, slotId }) => {
  const [ref, inView] = useInView();
  const isPriority = i <= 1 && (slotId === 'slots' || slotId === 'topContent');
  const { component: Component, clsPlaceholder: ClsPlaceholder } = getComponent(
    data.__typename || data._type
  );

  const renderImmediately = i <= 4; // Immediately load first 5 slots' content to avoid CLS issues.
  const renderMainComponent = renderImmediately || inView || !ClsPlaceholder;

  // if the component is "high on the page" OR has come into view due to user
  // interaction OR there is no skeleton provided for the component, then
  // render the main component
  if (renderMainComponent) {
    // Note that the component may still render a skeleton if it performs data fetching
    return (
      <Component
        priority={isPriority}
        deferred={!renderImmediately}
        {...data}
      />
    );
  }

  // Otherwise render the provided skeleton, which should be CLS performant
  return (
    <div ref={ref as Ref<HTMLDivElement>}>
      <ClsPlaceholder {...data} />
    </div>
  );
};

const MemoizedInView = memo(InView, (oldProps, newProps) => {
  return oldProps.i === newProps.i || oldProps.data.id === newProps.data.id;
});

export const getComponentForContent = (
  data: Maybe<{
    component?: Maybe<
      (Slot | ComponentWithFilters['component']) & {
        campaignId?: string;
        headingTag?: ManageHeadingTag;
        id?: Maybe<string>;
        _id?: Maybe<string>;
        _type?: Maybe<string>;
      }
    >;
    categoryId?: string;
    productId?: string;
    targetDevices?: Maybe<Array<Maybe<string>>>;
  }>,
  i: number,
  items: Maybe<GqlComponentWithFilters | ComponentWithFilters>[],
  slotId: string,
  headersObject: Record<string, ManageHeadingTag>
) => {
  if (!data?.component) return null;

  const { component } = data;

  const campaign = items[i]?.campaign;
  if (campaign) {
    component.campaignId = campaign[0]?.puid?.current || '';
  }

  const responsiveClasses = data.targetDevices?.map(device => {
    const classes = `${device}:block`;
    return classes;
  });

  const id = component.id || component._id;
  const type = component.__typename || component._type;

  component._type === 'FeaturesGrid'
    ? (component as FeaturesGridType).featureRows.forEach(
        featureRow =>
          (featureRow['headingTag'] = headersObject[featureRow['_key']])
      )
    : (component.headingTag =
        headersObject[(items[i] as GqlComponentWithFilters)?.id]);

  return (
    <div
      key={`${id}_${i}`}
      id={slotId && `${slotId}-${i}`}
      data-id={id}
      data-type={type}
      className={tw('hidden empty:hidden', responsiveClasses)}
    >
      <MemoizedInView i={i} data={component} slotId={slotId} />
    </div>
  );
};
